import { useRef, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useTranslation } from '@/lib/i18n';
import { motion } from 'motion/react';
import { cn } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { DiamondGrid, PhotoPlaceholder, diamondGlows } from '../primitives';
import { DRAG_TOUCH_ACTION, useCarouselDrag } from './use-carousel-drag';

/**
 * A point on the timeline. `caption` and `image` are OPTIONAL because the hand-off only spells out the
 * three milestones that fall inside its viewport — 2012, 2014 and 2017 — while the year rail lists
 * seven. The remaining four are drawn but never labelled, and no photograph is exported for any of
 * them (`background: url(.jpg)` in the CSS is an empty reference).
 *
 * A slide with no copy renders the placeholder and no caption, on purpose: an invented milestone would
 * be worse than a visibly unfinished one. Fill these in and the section is done.
 */
/**
 * A milestone's artwork, in the two shapes the hand-off uses — the same `kind` discriminator the
 * Solutions cards use, for the same reason: the two are rendered completely differently.
 *
 * `photo` FILLS the card, cropped. Its `src` is either one file or one per locale keyed by language with
 * `en` as the required fallback: 2025's is a phone showing the app, so its screen has to be in the
 * reader's language. That per-locale idiom is now in three places in this route group (the app-download
 * band and the solution tabs are the others) and is worth hoisting when a fourth appears.
 *
 * `illustration` sits ON the plate instead, CENTRED and nudged 9px above the card's middle, as the
 * export places it. Each one carries its own `width` because the two drawings are not the same shape —
 * the globe is near-square at 312x316, the terminal portrait at 255x318 — so a shared width would have
 * made one of them overflow. The globe's is the export's own; the terminal's is set so it overhangs the
 * card's 318px height by the same amount.
 *
 * `screen` is a product capture, and it is placed the other way round: the plate goes DARK and the
 * capture is anchored to the card's bottom-RIGHT corner so it bleeds off two edges instead of sitting
 * centred with air around it. That was read off the design rather than an export: the dark plate carries
 * the card's own aspect (1.403 against 1.390) and the capture the SVG's (1.431 against 1.440), which is
 * what identifies the dark shape as the CARD and not a device bezel drawn into the artwork.
 */
type MilestoneArt =
	| { kind: 'photo'; src: string | (Record<string, string> & { en: string }); alt: string }
	| { kind: 'illustration'; src: string; alt: string; width: string }
	| { kind: 'screen'; src: string; alt: string; width: string };

/**
 * `id` exists because `year` is NOT unique — 2025 holds two milestones — so it can no longer key the
 * list. It is deliberately not the caption: that is a translated string, and keying on it would remount
 * every slide on a language change.
 */
type Milestone = { id: string; year: string; caption?: string; image?: MilestoneArt };

/** A hook, not a constant: `t` only extracts and only re-runs on a language change inside a component. */
function useMilestones(): Milestone[] {
	const { t } = useLingui();
	return [
		{
			id: 'born',
			year: '2012',
			caption: t`We were born in Barcelona`,
			image: { kind: 'photo', src: '/redesign/about/barcelona-skyline.jpg', alt: t`Barcelona seen from above` },
		},
		{
			id: 'ecommerce',
			year: '2014',
			caption: t`We launched the Solidarity Euro for ecommerce`,
			image: { kind: 'photo', src: '/redesign/about/laptop-checkout.jpg', alt: t`Someone shopping online on a laptop` },
		},
		{
			id: 'stores',
			year: '2017',
			caption: t`We moved into physical stores with Charity Rounding Up`,
			image: {
				kind: 'photo',
				src: '/redesign/about/contactless-terminal.jpg',
				alt: t`A shopper paying by contactless card at a shop counter`,
			},
		},
		{
			id: 'million',
			year: '2018',
			caption: t`We reached one million euros raised`,
			image: {
				kind: 'illustration',
				src: '/redesign/about/terminal-coins.svg',
				alt: t`An illustration of euro coins dropping into a card terminal`,
				width: '60.2%', // 266 of 442: the height that overhangs as much as the globe does
			},
		},
		{
			id: 'expansion',
			year: '2022',
			caption: t`We expanded into Andorra and Portugal`,
			image: {
				kind: 'illustration',
				src: '/redesign/about/globe-hearts.svg',
				alt: t`An illustration of the globe with two hearts over Europe`,
				width: '70.6%', // the export's own 312 of 442
			},
		},
		{
			id: 'app',
			year: '2025',
			caption: t`We launched our app for donors`,
			// PER-LOCALE, and only `en` exists so far: the other four fall back to it until the captures
			// arrive. Reuses the alt the Solutions page already ships for the same screen.
			image: {
				kind: 'photo',
				src: { en: '/redesign/about/thanks-screen-en.jpg' },
				alt: t`The Worldcoo app thanking a donor for their donation`,
			},
		},
		{
			id: 'italy',
			// The SECOND 2025. The design gives that year two cards, which is why the rail below is a
			// year axis rather than a slide list.
			year: '2025',
			caption: t`We achieved our first success story in Italy`,
			image: {
				kind: 'photo',
				src: '/redesign/about/italy-flag.jpg',
				alt: t`The Italian flag flying against a blue sky`,
			},
		},
		{
			id: 'plus',
			year: '2026',
			caption: t`We launched Worldcoo Plus, a new service for NGOs`,
			image: {
				kind: 'screen',
				src: '/redesign/about/plus-dashboard.svg',
				alt: t`The Worldcoo Plus dashboard for NGOs`,
				width: '84.3%', // measured off the design: 458 of the card's 543
			},
		},
	];
}

/*
 * Slot geometry lives in the classes below rather than in constants, because it has to change with the
 * breakpoint and Tailwind is what knows the breakpoint:
 *
 *   - the horizontal step is a CSS variable, `--step`, so one transform serves both layouts: 843px at
 *     `xl` (centre of the canvas to centre of a side frame, from the export's `left: -344px`) and one
 *     card plus a gutter below it. A percentage inside `translate` resolves against the element's own
 *     width, which is exactly what the mobile step wants.
 *   - the 49px drop of the side slots (the export puts them at top 231 against the active card's 182)
 *     is an `xl:` class, and it rides the `translate` property while the step rides `transform`, so the
 *     two compose instead of overwriting each other.
 */

/** The rail's marker: an 8px brand disc with a 4px hole, which the hand-off draws as two stacked circles. */
function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * "Nuestros hitos" — the timeline carousel.
 *
 * Three cards are on screen at once: the active one centred at full size, and the previous and next
 * bleeding off either edge at 25%. The year rail bottom-right is the control, the active year is
 * repeated as a 168px figure bottom-left, and the track can be dragged.
 *
 * Every slide stays in the DOM at every width — the far ones at `opacity: 0` — because dragging has to
 * have something to pull into view. They are `pointer-events-none` instead, so an invisible card can
 * never take a click.
 */
export function Milestones() {
	const { t } = useLingui();
	const { language } = useTranslation();
	const milestones = useMilestones();
	const [active, setActive] = useState(1); // the hand-off opens on 2014
	const current = milestones[active];
	const last = milestones.length - 1;

	/**
	 * The rail is a YEAR AXIS, not one entry per slide: 2025 holds two milestones — the donor app and
	 * the first success story in Italy — and the design labels that year once. So the years are deduped,
	 * a click lands on the FIRST slide of its year, and the marker follows the active slide's year, which
	 * is what keeps it still while you drag between the two 2025 cards.
	 */
	const years = milestones.reduce<{ year: string; first: number }[]>((acc, milestone, i) => {
		if (!acc.some((entry) => entry.year === milestone.year)) acc.push({ year: milestone.year, first: i });
		return acc;
	}, []);
	const activeYear = years.findIndex((entry) => entry.year === current?.year);

	const track = useRef<HTMLDivElement>(null);

	/**
	 * The step, MEASURED rather than assumed: the two layouts have very different steps and reading the
	 * real gap between two slots avoids duplicating the breakpoint in JavaScript. Both slots ride the same
	 * drag transform, so the distance between them is unaffected mid-gesture.
	 */
	const { drag, dragged, panHandlers } = useCarouselDrag({
		active,
		last,
		onChange: setActive,
		step: () => {
			const el = track.current;
			if (!el) return 1;
			const [a, b] = [el.children[0], el.children[1]] as (HTMLElement | undefined)[];
			const measured = a && b ? Math.abs(b.getBoundingClientRect().left - a.getBoundingClientRect().left) : 0;
			return measured || el.clientWidth || 1;
		},
	});

	return (
		<section className="relative overflow-hidden bg-white">
			{/* Brand-orange texture in the top-right corner. The export's glow is a 777x501 ellipse whose
			    right edge sits 339px past the canvas edge and whose top is at -314px, so its centre lands at
			    (1390.5, -63.5) — expressed from the RIGHT edge here so it stays in the corner at any width. */}
			<DiamondGrid
				color="var(--color-brand)"
				opacity={0.95}
				masks={[diamondGlows({ x: 'calc(100% - 50px)', y: '-63px' })]}
			/>

			{/* No horizontal padding below `xl`: the mobile hand-off insets each block differently — the
			    heading and the card at 16% of the width, the year and the rail near the edge — and the card
			    HAS to be narrower than the viewport for the neighbours to show at both edges. */}
			<div className="relative mx-auto flex max-w-[1440px] flex-col gap-10 py-16 md:py-20 xl:h-[738px] xl:gap-0 xl:py-0">
				{/* Left-aligned and flush with the card on mobile; centred at the top of the band on desktop.
				    36px on mobile against the desktop 40: measured by normalising one heading's line width
				    against the capture's own content width, which is the only scale-free way to read it off a
				    screenshot whose zoom is unknown. Every section heading uses the same pair. */}
				<h2 className="text-ink ml-[16%] text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem] xl:absolute xl:top-[65px] xl:ml-0 xl:w-full xl:text-center">
					{t`Our milestones`}
				</h2>

				{/* `touch-action: pan-y` is the whole reason a phone can still scroll the page through this
				    section: it hands us the horizontal gesture and keeps the vertical one for the browser.
				    `touch-none` — which is what a draggable normally reaches for — would swallow both. */}
				<motion.div
					ref={track}
					{...panHandlers}
					style={{ x: drag, touchAction: DRAG_TOUCH_ACTION }}
					className="relative cursor-grab select-none [--step:calc(100%+2rem)] active:cursor-grabbing xl:absolute xl:inset-0 xl:block xl:[--step:843px]"
				>
					{milestones.map((milestone, i) => {
						const offset = i - active;
						const isActive = offset === 0;
						const isNeighbour = Math.abs(offset) === 1;
						return (
							<div
								key={milestone.id}
								// The ACTIVE slide is the one in flow below `xl` — it is what gives the track its
								// height, which depends on how many lines its caption takes. The rest are absolute.
								className={cn(
									'flex flex-col gap-[9px] transition-[transform,opacity] duration-500 ease-out',
									// 69% of the viewport at 16% from the left: the mobile hand-off centres the card and
									// keeps ~58px of gutter either side, which is what the neighbours show through.
									'ml-[16%] w-[69%]',
									isActive ? 'relative' : 'absolute top-0 left-0',
									!isActive && 'xl:translate-y-[49px]',
									offset < 0 ? 'xl:items-end' : 'xl:items-start',
									'xl:absolute xl:top-[182px] xl:left-1/2 xl:ml-[-221px] xl:w-[442px]',
									isNeighbour ? 'cursor-pointer' : !isActive && 'pointer-events-none',
								)}
								style={{
									transform: `translateX(calc(${offset} * var(--step)))`,
									opacity: isActive ? 1 : isNeighbour ? 0.25 : 0,
								}}
								// Clicking a neighbour walks the timeline; the rail stays the primary control, which
								// is why this is not a button — it would double every year in the tab order. Ignored
								// when the click is the tail end of a drag.
								onClick={isNeighbour ? () => !dragged.current && setActive(i) : undefined}
								aria-hidden={!isActive}
							>
								<div
									className={cn(
										// Every card is the SAME size below `xl` — the mobile hand-off fades the neighbours
										// but does not shrink them; the 0.69 reduction is a desktop-only idea.
										'relative aspect-[442/318] w-full overflow-hidden rounded-3xl transition-[width,height] duration-500 ease-out',
										// #FFEDDF is the hand-off's own plate colour behind the photos and drawings, the same
										// literal the hero's app card uses — see the note there. A `screen` inverts it: the
										// design puts the product capture on a dark plate so the UI itself reads.
										milestone.image?.kind === 'screen' ? 'bg-ink' : 'bg-[#FFEDDF]',
										isActive ? 'xl:aspect-auto xl:h-[318px] xl:w-[442px]' : 'xl:aspect-auto xl:h-[220px] xl:w-[305px]',
									)}
								>
									{!milestone.image ? (
										// No `label`: the primitive's own default covers it, and a msgid for scaffolding
										// that gets deleted the moment the photographs arrive would cost ten translations.
										<PhotoPlaceholder className="size-full" />
									) : milestone.image.kind === 'illustration' ? (
										// Centred, then lifted 9px of the card's 318 (hence 47.2% rather than 50%). The height
										// is left to the SVG's own ratio, which is what makes it overhang the top edge.
										<img
											src={milestone.image.src}
											alt={milestone.image.alt}
											style={{ width: milestone.image.width }}
											className="pointer-events-none absolute top-[47.2%] left-1/2 h-auto -translate-x-1/2 -translate-y-1/2"
										/>
									) : milestone.image.kind === 'screen' ? (
										// Anchored to the CORNER, not centred: the capture is meant to run off the card's right
										// and bottom edges, which the `overflow-hidden` above then crops.
										<img
											src={milestone.image.src}
											alt={milestone.image.alt}
											style={{ width: milestone.image.width }}
											className="pointer-events-none absolute right-0 bottom-0 h-auto"
										/>
									) : (
										<Image
											src={
												typeof milestone.image.src === 'string'
													? milestone.image.src
													: (milestone.image.src[language] ?? milestone.image.src.en)
											}
											alt={milestone.image.alt}
											height={318}
											aspectRatio={442 / 318}
											objectFit="cover"
											className="pointer-events-none size-full object-cover!"
										/>
									)}
								</div>
								{milestone.caption && (
									<p className="text-ink w-full text-base leading-[1.5] tracking-[-0.02em] xl:text-xl">
										{milestone.caption}
									</p>
								)}
							</div>
						);
					})}
				</motion.div>

				{/* The active year, repeated large: 168px on desktop and exactly half that on mobile, both
				    measured. Its 168px box overflows the band's bottom edge by 19px — the glyphs do not, and
				    the section clips anyway. */}
				<p
					aria-hidden
					className="text-brand ml-5 text-[5rem] leading-[1.3] font-medium tracking-[-0.02em] tabular-nums xl:absolute xl:top-[539px] xl:left-8 xl:ml-0 xl:text-[10.5rem]"
				>
					{current?.year}
				</p>

				{/* The rail. A grid rather than two rows of flex, so the marker sits in the active year's own
				    column and needs no measuring: `justify-self-center` puts it under that year whatever the
				    numeral's width. The line is one more grid item spanning every column. */}
				<div
					// 16px years on a 12px gap at BOTH sizes — measured ink-for-ink against the desktop capture,
					// the rail is not scaled down for mobile. Its 326px total is exactly what a 375px screen
					// leaves inside these gutters.
					className="ml-9 grid gap-x-3 self-start xl:absolute xl:right-8 xl:bottom-8 xl:ml-0 xl:self-auto"
					style={{ gridTemplateColumns: `repeat(${years.length}, auto)` }}
				>
					{years.map(({ year, first }, i) => (
						<button
							key={year}
							type="button"
							onClick={() => setActive(first)}
							aria-current={i === activeYear ? 'true' : undefined}
							style={{ gridRow: 1, gridColumn: i + 1 }}
							className={cn(
								'text-ink cursor-pointer text-base leading-[1.3] font-medium tracking-[-0.02em] transition-opacity',
								i === activeYear ? 'opacity-100' : 'opacity-50 hover:opacity-75',
							)}
						>
							{year}
						</button>
					))}
					<span
						aria-hidden
						style={{ gridRow: 2, gridColumn: '1 / -1' }}
						className="bg-brand/30 mt-[9px] h-px self-center"
					/>
					<span
						aria-hidden
						style={{ gridRow: 2, gridColumn: activeYear + 1 }}
						className="mt-[9px] self-center justify-self-center"
					>
						<YearMarker />
					</span>
				</div>
			</div>
		</section>
	);
}
